import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import styled from 'styled-components';
import { useEditor } from './EditorContext';
import { getOutputDuration, secondsToTimecode } from './editorUtils';

const Bar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 12px;
    background: #161625;
    border-top: 1px solid #2a2a3a;
    flex-shrink: 0;
`;

const TimecodeBox = styled.div`
    background: #0a0a12;
    border: 1px solid #2a2a3a;
    border-radius: 4px;
    padding: 2px 10px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    color: #64b5f6;
    letter-spacing: 1px;
    min-width: 100px;
    text-align: center;
`;

const iconBtnSx = { color: '#aaa', '&:hover': { color: '#fff' }, padding: '4px' };

/**
 * TransportControls — play/pause/step/rewind/ff buttons + timecode display.
 * Communicates through EditorContext.
 *
 * The video element is controlled imperatively via a ref passed as `videoRef`.
 */
export default function TransportControls({ videoRef }) {
    const { state, play, pause, setPlayhead } = useEditor();
    const { isPlaying, playheadTime, duration, cuts, videoStartOffset, videoEndOffset } = state;

    const outputDuration = getOutputDuration(duration, cuts);

    const handlePlayPause = () => {
        const el = videoRef?.current;
        if (!el) { console.warn('[VideoEditor] Play: videoRef is null'); return; }
        if (isPlaying) {
            el.pause();
            pause();
        } else {
            const offset = videoStartOffset || 0;
            const endOff = videoEndOffset || 0;
            const inIntro = offset > 0 && playheadTime < offset;
            const inOutro = endOff > 0 && playheadTime >= offset + outputDuration;
            if (inIntro || inOutro) {
                // Pre/post-roll region: set reducer state only.
                // ProgramMonitor's ticker effects handle playhead advancement.
                play();
            } else {
                el.play().then(() => {
                    play();
                }).catch((err) => {
                    console.error('[VideoEditor] play() rejected:', err.name, err.message);
                });
            }
        }
    };

    const handleStepBack = () => {
        const el = videoRef?.current;
        if (!el) return;
        const t = Math.max(0, el.currentTime - 1 / 25);
        el.currentTime = t;
        setPlayhead(t);
    };

    const handleStepForward = () => {
        const el = videoRef?.current;
        if (!el) return;
        const t = Math.min(el.duration || duration, el.currentTime + 1 / 25);
        el.currentTime = t;
        setPlayhead(t);
    };

    const handleRewind = () => {
        const el = videoRef?.current;
        if (!el) return;
        const t = Math.max(0, el.currentTime - 10);
        el.currentTime = t;
        setPlayhead(t);
    };

    const handleFF = () => {
        const el = videoRef?.current;
        if (!el) return;
        const t = Math.min(el.duration || duration, el.currentTime + 10);
        el.currentTime = t;
        setPlayhead(t);
    };

    const handleToStart = () => {
        const el = videoRef?.current;
        if (!el) return;
        el.currentTime = 0;
        setPlayhead(0);
    };

    const handleToEnd = () => {
        const el = videoRef?.current;
        if (!el) return;
        const t = el.duration || duration;
        el.currentTime = t;
        setPlayhead(t);
    };

    return (
        <Bar>
            <Tooltip title="Go to start">
                <IconButton sx={iconBtnSx} size="small" onClick={handleToStart}>
                    <SkipPreviousIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Rewind 10s">
                <IconButton sx={iconBtnSx} size="small" onClick={handleRewind}>
                    <FastRewindIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Step back 1 frame">
                <IconButton sx={iconBtnSx} size="small" onClick={handleStepBack}>
                    <SkipPreviousIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}>
                <IconButton
                    sx={{ ...iconBtnSx, color: '#64b5f6', '&:hover': { color: '#fff' }, padding: '6px' }}
                    onClick={handlePlayPause}
                >
                    {isPlaying ? (
                        <PauseIcon sx={{ fontSize: 26 }} />
                    ) : (
                        <PlayArrowIcon sx={{ fontSize: 26 }} />
                    )}
                </IconButton>
            </Tooltip>

            <Tooltip title="Step forward 1 frame">
                <IconButton sx={iconBtnSx} size="small" onClick={handleStepForward}>
                    <SkipNextIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Fast-forward 10s">
                <IconButton sx={iconBtnSx} size="small" onClick={handleFF}>
                    <FastForwardIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Tooltip title="Go to end">
                <IconButton sx={iconBtnSx} size="small" onClick={handleToEnd}>
                    <SkipNextIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Tooltip>

            <Box sx={{ width: 16 }} />

            <TimecodeBox>{secondsToTimecode(playheadTime, 25)}</TimecodeBox>

            <Typography variant="caption" sx={{ color: '#888', mx: 0.5 }}>/</Typography>

            <TimecodeBox style={{ color: '#a0a8c0', borderColor: '#3a3a55' }}>{secondsToTimecode(duration, 25)}</TimecodeBox>
        </Bar>
    );
}
